import { useEffect, useLayoutEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import InfoOutlinedIcon from '@mui/icons-material/InfoOutlined';
import { SampleDocumentLink } from 'src/components/SampleDocument';
import type { Tour, TourPlacement } from './tourSteps';

const CARD_W = 348;
const GAP = 14;
/** How often the spotlight re-measures its target (grid scroll, modal open…). */
const TRACK_MS = 120;

type Box = { top: number; left: number; width: number; height: number };

const sameBox = (a: Box | null, b: Box | null) =>
  !!a &&
  !!b &&
  Math.abs(a.top - b.top) < 1 &&
  Math.abs(a.left - b.left) < 1 &&
  Math.abs(a.width - b.width) < 1 &&
  Math.abs(a.height - b.height) < 1;

const clamp = (v: number, min: number, max: number) =>
  Math.max(min, Math.min(max, v));

const asList = (target?: string | string[]) =>
  !target ? [] : Array.isArray(target) ? target : [target];

/**
 * Rendered and actually visible — several targets are hidden with display:none.
 * Scans every match, not just the first: a selector may be a list ("the row is
 * processing OR finished") and the first match in DOM order can be the hidden
 * one.
 */
const onScreen = (selector: string) => {
  const els = Array.from(
    document.querySelectorAll(selector)
  ) as HTMLElement[];
  return els.some((el) => {
    const r = el.getBoundingClientRect();
    return !!(r.width || r.height);
  });
};

/**
 * Transient overlay layers that must NOT count as covering the spotlight: a
 * Select menu, popover or tooltip is opened FROM the panel being spotlighted, and
 * MUI portals it (plus an invisible full-viewport backdrop) over everything. Left
 * unhandled, opening the Product Grouping dropdown inside the Tactic Details
 * panel made the spotlight decide the panel was buried, jump to the row behind,
 * then jump back when the menu closed — flickering on every measure tick.
 *
 * Deliberately NOT `.MuiModal-root`: a Dialog carries that too, and a dialog
 * opening over a target genuinely should stand the spotlight down.
 */
const TRANSIENT_LAYERS = [
  '.MuiPopover-root',
  '.MuiMenu-root',
  '.MuiTooltip-popper',
  '.MuiAutocomplete-popper',
  '.MuiBackdrop-invisible'
].join(', ');

/** Ticks a target may look buried before the spotlight actually stands down. */
const MISS_TOLERANCE = 3;

/**
 * Resolve where the card sits relative to the spotlight hole. `auto` prefers
 * below → above → right → left, picking the first side with room so the card is
 * never pushed off screen (which is what happens with a fixed placement on a
 * short viewport). Returns null when no side has room — a target that nearly
 * fills the viewport, e.g. a spotlighted modal — so the caller can dock instead.
 */
const resolvePlacement = (
  hole: Box,
  cardH: number,
  requested: TourPlacement = 'auto'
): Exclude<TourPlacement, 'auto'> | null => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const fits = {
    bottom: hole.top + hole.height + GAP + cardH <= vh - 12,
    top: hole.top - GAP - cardH >= 12,
    right: hole.left + hole.width + GAP + CARD_W <= vw - 12,
    left: hole.left - GAP - CARD_W >= 12
  };
  if (requested !== 'auto' && fits[requested]) return requested;
  if (fits.bottom) return 'bottom';
  if (fits.top) return 'top';
  if (fits.right) return 'right';
  if (fits.left) return 'left';
  return null;
};

/** Card position when it can't sit beside the spotlight (or there isn't one). */
const dockedPosition = (
  corner: NonNullable<Tour['steps'][number]['cardCorner']> = 'bottom-right',
  cardH: number
) => {
  const vw = window.innerWidth;
  const vh = window.innerHeight;
  const M = 20;
  const right = Math.max(12, vw - CARD_W - M);
  const bottom = Math.max(12, vh - cardH - M);
  switch (corner) {
    case 'bottom-left':
      return { left: M, top: bottom };
    case 'top-left':
      return { left: M, top: M };
    case 'top-right':
      return { left: right, top: M };
    default:
      return { left: right, top: bottom };
  }
};

type Props = {
  tour: Tour;
  stepIndex: number;
  /** Position shown to the user — conditional steps that were dropped don't count. */
  stepNumber: number;
  stepTotal: number;
  onNext: () => void;
  /** Advance past a conditional step whose condition doesn't hold. */
  onSkipStep: () => void;
  onClose: () => void;
};

function TourOverlay({
  tour,
  stepIndex,
  stepNumber,
  stepTotal,
  onNext,
  onSkipStep,
  onClose
}: Props) {
  const step = tour.steps[stepIndex];
  const isLast = stepIndex === tour.steps.length - 1;

  const [hole, setHole] = useState<Box | null>(null);
  /**
   * The matched target's OWN box, before `spotlightAncestor` widens the hole out
   * to its record. A step about a whole table row lights the row, but the thing
   * to press is still one button inside it — this is what the click hint aims
   * at, so it can never end up ringing the middle of a row.
   */
  const [mark, setMark] = useState<Box | null>(null);
  const [cardH, setCardH] = useState(190);
  /**
   * The gesture demo has served its purpose — the user has pressed on the page, so
   * they are drawing. Leaving the loop running would then be a phantom box moving
   * about while they work.
   */
  const [gestureDone, setGestureDone] = useState(false);
  const cardRef = useRef<HTMLDivElement>(null);
  const targets = asList(step.target);
  const targetKey = targets.join('|');
  // An ACTION step waits for the user to do the thing (no Next to skip it with);
  // an explanatory step has nothing to wait for and gets a Next.
  const hasSignal = !!(
    step.advanceWhenVisible ||
    step.advanceWhenGone ||
    step.advanceWhenRoute
  );

  // A conditional step (`onlyWhenVisible`) runs only in the state it describes
  // — the engine warming up, say — and is dropped otherwise. Decided in a
  // layout effect, before paint, so a step that doesn't apply never flashes its
  // card. Checked once on entry: once a step is showing it stays, even if the
  // condition it opened on stops holding (services finishing their power-on
  // must not yank the card out from under the user mid-read; the step has its
  // own advance signal for that).
  // Held in a ref rather than recomputed each render so every effect below sees
  // the same verdict for the whole life of the step.
  const conditions = asList(step.onlyWhenVisible);
  const verdict = useRef({ index: -1, applies: true });
  if (verdict.current.index !== stepIndex) {
    verdict.current = {
      index: stepIndex,
      applies: !conditions.length || conditions.some(onScreen)
    };
  }
  const applies = verdict.current.applies;
  useLayoutEffect(() => {
    if (!applies) onSkipStep();
  }, [stepIndex, applies]);

  // Retire the gesture demo on the first press anywhere on the page — that is the
  // user starting the drag. Capture phase, so a handler that stops propagation
  // (the PDF's own selection layer does) can't hide the signal from us.
  useEffect(() => {
    setGestureDone(false);
    if (!step.gestureHint) return;
    const onDown = () => setGestureDone(true);
    window.addEventListener('pointerdown', onDown, true);
    return () => window.removeEventListener('pointerdown', onDown, true);
  }, [stepIndex, step.gestureHint]);

  // Bring the target into view when the step changes. Its position is then
  // tracked continuously below, so a smooth scroll can't desync the spotlight.
  useEffect(() => {
    setHole(null);
    if (!applies) return;
    const first = targets.find((t) => document.querySelector(t));
    if (!first) return;
    const el = document.querySelector(first) as HTMLElement | null;
    if (el) el.scrollIntoView({ block: 'center', inline: 'nearest' });
  }, [stepIndex, targetKey]);

  // Track the target's live geometry. An interval (rather than one measurement)
  // keeps the spotlight glued to elements that move for reasons we get no event
  // for: ag-Grid row virtualisation, a dialog animating in, a status pill
  // re-rendering and reflowing the actions row.
  useEffect(() => {
    const clear = () => {
      setHole((prev) => (prev === null ? prev : null));
      setMark((prev) => (prev === null ? prev : null));
    };

    /**
     * Is something covering this element? Acting on a step often opens a panel
     * over the control that opened it, and a spotlight that kept cutting a hole
     * through that panel would look wrong AND — since the mask blocks everything
     * outside the hole — trap the user. A covered target is skipped, so a step
     * whose target list includes the panel spotlights the panel instead. Our own
     * overlay never counts as covering.
     */
    const isCovered = (el: HTMLElement, r: DOMRect) => {
      const atPoint = document.elementFromPoint(
        r.left + r.width / 2,
        r.top + r.height / 2
      ) as HTMLElement | null;
      if (!atPoint) return true;
      if (atPoint.closest?.('.ktour-card, .ktour-mask, .ktour-arrow')) {
        return false;
      }
      if (atPoint.closest?.(TRANSIENT_LAYERS)) return false;
      return !el.contains(atPoint) && !atPoint.contains(el);
    };

    let misses = 0;

    const measure = () => {
      if (!targets.length || !applies) {
        clear();
        return;
      }

      // Highest-priority target that is on screen and not buried wins. Scanning
      // from the top every tick (rather than sticking to whatever was used last)
      // is what lets a step follow the user into a panel as it opens.
      let found: { el: HTMLElement; r: DOMRect } | null = null;
      for (const selector of targets) {
        const el = document.querySelector(selector) as HTMLElement | null;
        if (!el) continue;
        const r = el.getBoundingClientRect();
        if (!r.width && !r.height) continue;
        if (isCovered(el, r)) continue;
        found = { el, r };
        break;
      }
      if (!found) {
        // Ride out a brief "everything looks buried" blip — a menu animating in,
        // a re-render between frames — instead of tearing the spotlight down and
        // rebuilding it (which restarts its pulse and moves the card).
        misses += 1;
        if (misses >= MISS_TOLERANCE) clear();
        return;
      }
      misses = 0;

      let r = found.r;
      const own: Box = {
        top: found.r.top,
        left: found.r.left,
        width: found.r.width,
        height: found.r.height
      };
      setMark((prev) => (sameBox(prev, own) ? prev : own));
      // Widen to the record the target sits in (see `spotlightAncestor`). Measured
      // here rather than resolved as a target so the on-screen and covered checks
      // above still run against the small, specific element.
      if (step.spotlightAncestor) {
        const ancestor = found.el.closest(step.spotlightAncestor) as HTMLElement | null;
        if (ancestor) {
          const ar = ancestor.getBoundingClientRect();
          if (ar.width || ar.height) r = ar;
        }
      }
      const pad = step.spotlightPadding ?? 6;
      const vw = window.innerWidth;
      const vh = window.innerHeight;
      const next: Box = {
        top: clamp(r.top - pad, 0, vh),
        left: clamp(r.left - pad, 0, vw),
        width: Math.min(r.width + pad * 2, vw),
        height: Math.min(r.height + pad * 2, vh)
      };
      setHole((prev) => (sameBox(prev, next) ? prev : next));
    };

    measure();
    const iv = setInterval(measure, TRACK_MS);
    window.addEventListener('resize', measure);
    window.addEventListener('scroll', measure, true);
    return () => {
      clearInterval(iv);
      window.removeEventListener('resize', measure);
      window.removeEventListener('scroll', measure, true);
    };
  }, [stepIndex, targetKey, step.spotlightPadding, step.spotlightAncestor]);

  // Card height feeds placement, so measure it after every content change.
  useLayoutEffect(() => {
    const h = cardRef.current?.offsetHeight;
    if (h && h !== cardH) setCardH(h);
  }, [stepIndex, hole, cardH]);

  // Steps whose subject is "this panel is now open" open it themselves, retrying
  // until the target exists — after an upload the grid needs a refresh cycle
  // before the new row (and its Tactic Details control) is there to click.
  useEffect(() => {
    const triggers = asList(step.autoOpen);
    // A step being skipped must not still click things on the way past.
    if (!triggers.length || !applies) return;
    const primary = targets[0];
    let done = false;
    const attempt = () => {
      if (done) return;
      // Already open (e.g. the user got there first) — don't reopen it.
      if (primary && onScreen(primary)) {
        done = true;
        return;
      }
      const el = triggers
        .map((t) => document.querySelector(t) as HTMLElement | null)
        .find(Boolean);
      if (!el) return;
      done = true;
      el.click();
    };
    attempt();
    const iv = setInterval(attempt, 400);
    const giveUp = setTimeout(() => {
      done = true;
    }, 60000);
    return () => {
      clearInterval(iv);
      clearTimeout(giveUp);
    };
  }, [stepIndex, step.autoOpen, targetKey]);

  // The card has no Back/Next — the tour moves when the user actually does the
  // step. `advanceWhenGone` only fires after the element has been seen, so
  // entering a step whose element isn't mounted yet can't skip it instantly.
  //
  // "On screen" means rendered AND visible: several of these controls (the row's
  // Process link, the selected-file list) are toggled with `display:none` rather
  // than unmounted, and a presence-only check would sit there waiting for a DOM
  // removal that only arrives on the next 10s grid poll.
  useEffect(() => {
    if (!hasSignal || !applies) return;
    let seen = false;
    const iv = setInterval(() => {
      if (
        step.advanceWhenRoute &&
        window.location.pathname.includes(step.advanceWhenRoute)
      ) {
        onNext();
        return;
      }
      if (step.advanceWhenVisible && onScreen(step.advanceWhenVisible)) {
        onNext();
        return;
      }
      if (step.advanceWhenGone) {
        if (onScreen(step.advanceWhenGone)) seen = true;
        else if (seen) onNext();
      }
    }, 250);
    return () => clearInterval(iv);
  }, [
    stepIndex,
    hasSignal,
    step.advanceWhenVisible,
    step.advanceWhenGone,
    step.advanceWhenRoute,
    onNext
  ]);

  // ---- geometry -------------------------------------------------------------
  const vw = typeof window !== 'undefined' ? window.innerWidth : 0;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 0;

  // No spotlight, or one that leaves no room beside it → dock in a corner.
  // Centring the card would drop it right on top of whatever the step is asking
  // the user to fill in.
  const docked = dockedPosition(step.cardCorner, cardH);
  let cardLeft = docked.left;
  let cardTop = docked.top;
  let placement: Exclude<TourPlacement, 'auto'> | null = null;
  let arrowLeft: number | null = null;

  if (hole) {
    placement = resolvePlacement(hole, cardH, step.placement);
    if (placement === null) {
      // keep the docked position
    } else if (placement === 'bottom' || placement === 'top') {
      cardLeft = hole.left + hole.width / 2 - CARD_W / 2;
      cardTop =
        placement === 'bottom'
          ? hole.top + hole.height + GAP
          : hole.top - GAP - cardH;
    } else {
      cardTop = hole.top + hole.height / 2 - cardH / 2;
      cardLeft =
        placement === 'right'
          ? hole.left + hole.width + GAP
          : hole.left - GAP - CARD_W;
    }
    // Clamp before the arrow is placed, so it still points at the target from
    // the card's real position.
    cardLeft = clamp(cardLeft, 12, Math.max(12, vw - CARD_W - 12));
    cardTop = clamp(cardTop, 12, Math.max(12, vh - cardH - 12));
    if (placement === 'bottom' || placement === 'top') {
      arrowLeft = clamp(
        hole.left + hole.width / 2 - 6,
        cardLeft + 16,
        cardLeft + CARD_W - 28
      );
    }
  }

  // Final guard covering the docked path too: the card must always be fully on
  // screen, whatever the target geometry did.
  cardLeft = clamp(cardLeft, 12, Math.max(12, vw - CARD_W - 12));
  cardTop = clamp(cardTop, 12, Math.max(12, vh - cardH - 12));

  const showMissingHint = !!targets.length && !hole && !!step.missingHint;
  /** Spread onto every mask panel; empty unless the step opts out of the fence. */
  const maskPassThrough = step.allowPageInteraction
    ? ({ pointerEvents: 'none' } as const)
    : undefined;

  /**
   * Where to loop the gesture demo: a modest patch inside the spotlight, kept off
   * the very edge so it reads as "somewhere on this page" rather than as a control
   * to press. Sized from the hole so it stays sensible at any pane width, with
   * absolute bounds so it is neither a speck nor a wall.
   */
  const gestureBox =
    hole && step.gestureHint === 'drag-region' && !gestureDone
      ? {
          left: hole.left + Math.max(24, hole.width * 0.1),
          top: hole.top + Math.max(48, hole.height * 0.22),
          width: clamp(hole.width * 0.34, 120, 260),
          height: clamp(hole.height * 0.1, 34, 70)
        }
      : null;

  /**
   * Where to put the "press this" hint: on the matched control itself, never on
   * the widened hole. On a step that lights a whole table row the hole is the
   * row, and a pulse around it would say nothing about WHICH of the row's four
   * controls to use.
   */
  const tapBox =
    (mark ?? hole) && step.gestureHint === 'click' && !gestureDone
      ? (mark ?? hole)
      : null;

  return createPortal(
    <>
      {/* Four panels instead of one full-screen sheet: the hole is genuinely
          empty, so the spotlighted control stays clickable and the user can do
          the step for real while the tour explains it.

          `allowPageInteraction` drops the fence entirely — see the field's note in
          tourSteps. The dim stays for focus, but nothing is blocked, which is what
          a step needs when its action is a free-form drag rather than one click. */}
      {hole ? (
        <>
          <div
            className="ktour-mask"
            style={{
              top: 0,
              left: 0,
              right: 0,
              height: Math.max(0, hole.top),
              ...maskPassThrough
            }}
          />
          <div
            className="ktour-mask"
            style={{
              top: hole.top + hole.height,
              left: 0,
              right: 0,
              bottom: 0,
              ...maskPassThrough
            }}
          />
          <div
            className="ktour-mask"
            style={{
              top: hole.top,
              left: 0,
              width: Math.max(0, hole.left),
              height: hole.height,
              ...maskPassThrough
            }}
          />
          <div
            className="ktour-mask"
            style={{
              top: hole.top,
              left: hole.left + hole.width,
              right: 0,
              height: hole.height,
              ...maskPassThrough
            }}
          />
          <div
            className="ktour-ring"
            style={{
              top: hole.top,
              left: hole.left,
              width: hole.width,
              height: hole.height,
              borderRadius: step.spotlightRadius ?? 12
            }}
          />
          {/* Gesture demo: a dashed box drawing itself under a moving pointer, so
              "drag a box" is shown rather than only described. Never interactive —
              this step's whole point is that the user's own drag gets through. */}
          {gestureBox && (
            <div className="ktour-gesture" style={gestureBox}>
              <div className="ktour-gesture-box" />
              <div className="ktour-gesture-dot" />
            </div>
          )}
          {/* "Press this one" — a pulsing outline on the control plus a pointer
              tapping it. Sits over the button but never takes the click:
              pointer-events are off throughout, so the real press goes through
              to the button underneath. */}
          {tapBox && (
            <div
              className="ktour-tap"
              aria-hidden="true"
              style={{
                top: tapBox.top,
                left: tapBox.left,
                width: tapBox.width,
                height: tapBox.height
              }}
            >
              <span className="ktour-tap-ring" />
              <span className="ktour-tap-cursor" />
            </div>
          )}
        </>
      ) : (
        // Target not on screen yet (upload panel closed, no rows in the grid…).
        // The dim stays, but it must NOT swallow clicks: the user has to be able
        // to do the thing the step is describing — opening the upload panel,
        // scrolling the table — for the step to ever resolve.
        <div
          className="ktour-mask is-idle"
          style={{ inset: 0, pointerEvents: 'none' }}
        />
      )}

      {arrowLeft !== null && (
        <div
          className={'ktour-arrow' + (placement === 'top' ? ' is-below' : '')}
          style={{
            left: arrowLeft,
            top: placement === 'bottom' ? cardTop - 6 : cardTop + cardH - 6
          }}
        />
      )}

      <div
        ref={cardRef}
        className="ktour-card"
        style={{ top: cardTop, left: cardLeft }}
        role="dialog"
        aria-label={`${tour.name} tour, step ${stepNumber}`}
        // Don't take focus on click: steps 1-2 spotlight controls inside a MUI
        // Dialog, whose focus trap would fight the card for focus on every press.
        onMouseDown={(e) => e.preventDefault()}
      >
        <div className="ktour-top">
          <span className="ktour-step">
            <i>{stepNumber}</i>
            Step {stepNumber} of {stepTotal}
          </span>
        </div>

        <h4>{step.title}</h4>
        <p>{step.body}</p>

        {showMissingHint && (
          <div className="ktour-hint">
            <InfoOutlinedIcon />
            <span>{step.missingHint}</span>
          </div>
        )}

        {/* The way out for someone who reached "upload a document" without one. */}
        {step.offerSampleDocument && <SampleDocumentLink inCard />}

        <div className="ktour-foot">
          {/* Skip is the only way out now that the card has no close icon, so it
              stays available on every step except the last (where Done ends it). */}
          {!isLast && (
            <button type="button" className="ktour-skip" onClick={onClose}>
              Skip tour
            </button>
          )}
          {/* Action steps get no Next — a button there would step over the very
              thing the tour is waiting for; they advance when the user acts.
              Explanatory steps (nothing to do but read) get one, otherwise the
              tour could not move at all. Back is deliberately absent everywhere. */}
          {!hasSignal && (
            <button
              type="button"
              className="ktour-btn primary"
              onClick={isLast ? onClose : onNext}
            >
              {isLast ? 'Done' : 'Next'}
            </button>
          )}
        </div>
      </div>
    </>,
    document.body
  );
}

export default TourOverlay;
